import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent } from '@tark/shared/components';
import type {
  BuzzFeedV1Block,
  BuzzFeedV1Item,
  BuzzFeedV1Response,
  BuzzLiveRefreshResponse,
} from '@tark/shared/types';
import { getWebBuzzFeed, getWebBuzzLiveRefresh } from '@tark/shared/lib/home';
import {
  buildBoundedBuzzBodyFromPages,
  getBuzzBlockRenderKey,
  getNextBoundedBuzzPageParam,
} from '../lib/buzzFeedPaging';
import {
  getBuzzAdHostMode,
  getBuzzAdSenseConfig,
  isBuzzWebAdsEnabled,
  prepareBuzzAdSense,
} from '../lib/googleAdPrivacy';
import {
  ArrowRight,
  Clock,
  ExternalLink,
  Flame,
  GraduationCap,
  MapPin,
  Newspaper,
  PlayCircle,
  Radio,
  RefreshCw,
  Trophy,
  Users,
} from 'lucide-react';
import { AppPageFrame } from '../components/AppPageFrame';

type BuzzViewFilter =
  | 'for_you'
  | 'live'
  | 'results'
  | 'news'
  | 'videos'
  | 'lessons'
  | 'rankings'
  | 'local';

type BuzzSportFilter = 'ALL' | 'TENNIS' | 'PICKLEBALL';
type BuzzScopeFilter = 'ALL' | 'LOCAL';
type BuzzListItem =
  | {
      kind: 'ARTICLE';
      id: string;
      item: BuzzFeedV1Item;
    }
  | {
      kind: 'AD';
      id: string;
      slotIndex: number;
    };

const VIEW_CHIPS: Array<{
  key: BuzzViewFilter;
  label: string;
  icon?: typeof Radio;
}> = [
  { key: 'for_you', label: 'For you' },
  { key: 'live', label: 'Live', icon: Radio },
  { key: 'results', label: 'Results', icon: Trophy },
  { key: 'news', label: 'News', icon: Newspaper },
  { key: 'videos', label: 'Videos', icon: PlayCircle },
  { key: 'lessons', label: 'Lessons', icon: GraduationCap },
  { key: 'rankings', label: 'Rankings', icon: Users },
  { key: 'local', label: 'Local' },
];

const FEED_STALE_TIME_MS = 60 * 1000;
const LIVE_REFRESH_INTERVAL_MS = 60 * 1000;
const LIVE_REFRESH_LIMIT = 12;
const BUZZ_AD_INSERTION_INTERVAL = 8;
const HOTLINK_BLOCKED_BUZZ_IMAGE_HOSTS = new Set(['cdn1.tennishead.net']);

export default function BuzzPage() {
  const navigate = useNavigate();
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [viewFilter, setViewFilter] = useState<BuzzViewFilter>('for_you');
  const [includeTennis, setIncludeTennis] = useState(true);
  const [includePickleball, setIncludePickleball] = useState(true);
  const [nearMeOnly, setNearMeOnly] = useState(true);
  const isDocumentVisible = useDocumentVisibility();

  const sportFilter = useMemo<BuzzSportFilter>(() => {
    if (includeTennis && includePickleball) {
      return 'ALL';
    }
    if (includeTennis) {
      return 'TENNIS';
    }
    if (includePickleball) {
      return 'PICKLEBALL';
    }
    return 'ALL';
  }, [includePickleball, includeTennis]);

  const scopeFilter = useMemo<BuzzScopeFilter>(() => {
    return nearMeOnly || viewFilter === 'local' ? 'LOCAL' : 'ALL';
  }, [nearMeOnly, viewFilter]);

  const feedQuery = useInfiniteQuery<BuzzFeedV1Response>({
    queryKey: ['buzz-page-feed', viewFilter, sportFilter, scopeFilter],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      getWebBuzzFeed({
        view: viewFilter,
        sport: sportFilter,
        scope: scopeFilter,
        cursor: typeof pageParam === 'string' ? pageParam : null,
        signal,
        telemetry: {
          trigger: pageParam ? 'pagination' : 'page',
          screenVisible: isDocumentVisible,
        },
      }),
    getNextPageParam: getNextBoundedBuzzPageParam,
    staleTime: FEED_STALE_TIME_MS,
    retry: false,
  });

  const pages = feedQuery.data?.pages;
  const firstPage = pages?.[0] ?? null;
  const liveSeedItemCount =
    (firstPage?.top?.pulseStrip?.items?.length ?? 0) + (firstPage?.top?.liveRail?.items?.length ?? 0);
  const shouldPollLive = isDocumentVisible && (viewFilter === 'live' || liveSeedItemCount > 0);

  const liveQuery = useQuery<BuzzLiveRefreshResponse>({
    queryKey: ['buzz-page-live', sportFilter, scopeFilter],
    queryFn: ({ signal }) =>
      getWebBuzzLiveRefresh({
        limit: LIVE_REFRESH_LIMIT,
        sport: sportFilter,
        scope: scopeFilter,
        signal,
        telemetry: {
          trigger: 'buzz_page',
          screenVisible: isDocumentVisible,
        },
      }),
    staleTime: FEED_STALE_TIME_MS,
    refetchInterval: shouldPollLive ? LIVE_REFRESH_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    retry: false,
    enabled: shouldPollLive,
  });

  const topBlocks = useMemo(() => {
    const mergedTop = mergeBuzzTopSection(firstPage?.top ?? null, liveQuery.data ?? null);

    return {
      pulseStrip: filterBlock(mergedTop?.pulseStrip, () => true, shouldShowTop('pulseStrip', viewFilter)),
      liveRail: filterBlock(mergedTop?.liveRail, () => true, shouldShowTop('liveRail', viewFilter)),
      hero: filterBlock(mergedTop?.hero, () => true, shouldShowTop('hero', viewFilter)),
    };
  }, [firstPage?.top, liveQuery.data, viewFilter]);

  const bodyBlocks = useMemo(
    () =>
      buildBoundedBuzzBodyFromPages(pages ?? [])
        .filter((block) => shouldShowBody(block, viewFilter))
        .map((block) => filterBlock(block, () => true, true))
        .filter((block): block is BuzzFeedV1Block => block !== null),
    [pages, viewFilter]
  );
  const mainBodyBlocks = useMemo(
    () => (viewFilter === 'rankings' ? bodyBlocks : bodyBlocks.filter((block) => block.blockType !== 'RANKINGS_BLOCK')),
    [bodyBlocks, viewFilter]
  );

  const hasVisibleContent = Boolean(
    topBlocks.pulseStrip?.items?.length ||
      topBlocks.liveRail?.items?.length ||
      topBlocks.hero?.items?.length ||
      bodyBlocks.length
  );

  const updatedAt = liveQuery.data?.generatedAt ?? firstPage?.generatedAt ?? null;
  const canShowInFeedAds = isBuzzWebAdsEnabled();

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const isVisible = entries.some((entry) => entry.isIntersecting);
        if (isVisible && feedQuery.hasNextPage && !feedQuery.isFetchingNextPage) {
          void feedQuery.fetchNextPage();
        }
      },
      {
        rootMargin: '320px 0px',
        threshold: 0.01,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feedQuery]);

  const openItem = (item: BuzzFeedV1Item) => {
    const url = resolveBuzzFeedItemUrl(item);
    if (!url) {
      return;
    }

    if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (url.startsWith('/')) {
      navigate(url);
    }
  };

  const toggleSport = (sport: 'tennis' | 'pickleball') => {
    if (sport === 'tennis') {
      const next = !includeTennis;
      setIncludeTennis(next || !includePickleball);
      return;
    }

    const next = !includePickleball;
    setIncludePickleball(next || !includeTennis);
  };

  if (feedQuery.isLoading && !firstPage) {
    return <BuzzPageLoadingState />;
  }

  if (feedQuery.isError && !firstPage) {
    return (
      <AppPageFrame className="space-y-6">
        <Card variant="default" className="rounded-[28px] border-red-200 bg-red-50">
          <CardContent className="space-y-4 p-6">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-red-600">Buzz unavailable</p>
              <h1 className="text-2xl font-semibold tracking-tight text-red-900">We could not load the sports feed</h1>
              <p className="text-sm text-red-700">
                Try again and we’ll reload live scores, rankings, highlights, and the latest tennis and pickleball stories.
              </p>
            </div>
            <Button onClick={() => void feedQuery.refetch()}>Retry</Button>
          </CardContent>
        </Card>
      </AppPageFrame>
    );
  }

  return (
    <AppPageFrame>
      <div className="overflow-hidden rounded-lg border border-[#dfd9ca] bg-[#fbfaf4] shadow-[0_18px_48px_-36px_rgba(15,23,42,0.6)]">
        <BuzzLiveTicker
          pulseBlock={topBlocks.pulseStrip}
          liveBlock={topBlocks.liveRail}
          updatedAt={updatedAt}
          isRefreshing={liveQuery.isFetching}
          onOpenItem={openItem}
        />

        <section className="border-b border-[#e7e1d3] px-4 py-4 sm:px-5 lg:px-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="space-y-1.5">
              <h1 className="text-[40px] font-black leading-none text-[#101010] sm:text-[54px]">Buzz</h1>
              <p className="max-w-xl text-sm font-medium text-[#6f6a5f]">Tennis and pickleball, live and local.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-[#6f6a5f]">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#ded8ca] bg-white px-3 py-2">
                <Clock className="h-3.5 w-3.5" />
                {updatedAt ? `Updated ${formatRelativeTime(updatedAt)}` : 'Fresh feed'}
              </span>
              {liveQuery.isFetching ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-[#ded8ca] bg-white px-3 py-2">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Refreshing
                </span>
              ) : null}
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
            <div className="-mx-1 overflow-x-auto pb-1">
              <div className="flex min-w-max gap-2 px-1">
                {VIEW_CHIPS.map((chip) => {
                  const Icon = chip.icon;
                  const active = chip.key === viewFilter;
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setViewFilter(chip.key)}
                      className={
                        active
                          ? 'inline-flex items-center gap-2 rounded-full border border-[#111] bg-[#111] px-4 py-2 text-sm font-bold text-white shadow-sm'
                          : 'inline-flex items-center gap-2 rounded-full border border-[#ded8ca] bg-white px-4 py-2 text-sm font-bold text-[#5f615c] transition-colors hover:border-[#111] hover:text-[#111]'
                      }
                    >
                      {Icon ? <Icon className="h-4 w-4" /> : null}
                      {chip.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <FilterPill label="Tennis" selected={includeTennis} onClick={() => toggleSport('tennis')} />
              <FilterPill label="Pickleball" selected={includePickleball} onClick={() => toggleSport('pickleball')} />
              <FilterPill
                label="Near me"
                selected={nearMeOnly}
                icon={<MapPin className="h-3.5 w-3.5" />}
                onClick={() => setNearMeOnly((current) => !current)}
              />
            </div>
          </div>
        </section>

        {liveQuery.isError ? (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800 sm:px-5 lg:px-6">
            Live scores may be delayed. Stories are still current.
          </div>
        ) : null}

        {!hasVisibleContent && !feedQuery.isFetching ? (
          <div className="p-4 sm:p-5 lg:p-6">
            <BuzzPageEmptyState onReset={() => setViewFilter('for_you')} />
          </div>
        ) : (
          <div className="p-3 sm:p-4 lg:p-5">
            <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px] 2xl:grid-cols-[minmax(0,1fr)_380px]">
              <main className="min-w-0 space-y-6">
                <BuzzHeroBlock block={topBlocks.hero} onOpenItem={openItem} />

                {mainBodyBlocks.map((block) => (
                  <BuzzPageBodyBlock
                    key={getBuzzBlockRenderKey(block)}
                    block={block}
                    onOpenItem={openItem}
                    showAds={canShowInFeedAds}
                  />
                ))}
              </main>

              <BuzzRightRail
                pulseBlock={topBlocks.pulseStrip}
                liveBlock={topBlocks.liveRail}
                bodyBlocks={bodyBlocks}
                updatedAt={updatedAt}
                showAds={canShowInFeedAds}
                onOpenItem={openItem}
              />
            </div>

            <div ref={loadMoreRef} data-testid="buzz-load-more-sentinel" className="h-1" />

            {feedQuery.isFetchingNextPage ? (
              <div className="mt-5 flex items-center justify-center gap-3 rounded-lg border border-[#dfd9ca] bg-white px-4 py-4 text-sm font-semibold text-[#6f6a5f]">
                <RefreshCw className="h-4 w-4 animate-spin" />
                Loading more stories
              </div>
            ) : null}

            {!feedQuery.hasNextPage && hasVisibleContent ? (
              <div className="mt-5 rounded-lg border border-[#dfd9ca] bg-white/70 px-4 py-4 text-sm font-semibold text-[#6f6a5f]">
                You&apos;re caught up. Refresh this page for new stories.
              </div>
            ) : null}
          </div>
        )}
      </div>
    </AppPageFrame>
  );
}

function BuzzPageLoadingState() {
  return (
    <AppPageFrame className="space-y-6">
      <div className="rounded-lg border border-[#dfd9ca] bg-[#fbfaf4] p-4 sm:p-5">
        <div className="h-3 w-24 rounded-full bg-[#ded8ca]" />
        <div className="mt-3 h-12 w-40 rounded-lg bg-[#ded8ca]" />
        <div className="mt-3 h-4 w-full max-w-lg rounded-full bg-[#ece7da]" />
      </div>

      <div className="rounded-lg border border-[#dfd9ca] bg-white p-4 sm:p-5">
        <div className="flex gap-2 overflow-hidden">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-10 w-28 shrink-0 rounded-full bg-[#ece7da]" />
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div className="h-[430px] rounded-lg border border-[#dfd9ca] bg-white/80" />
        <div className="grid gap-4 md:grid-cols-2">
          <div className="h-72 rounded-lg border border-[#dfd9ca] bg-white/80" />
          <div className="h-72 rounded-lg border border-[#dfd9ca] bg-white/80" />
        </div>
      </div>
    </AppPageFrame>
  );
}

function BuzzPageEmptyState({ onReset }: { onReset: () => void }) {
  return (
    <Card variant="default" className="rounded-lg border-[#dfd9ca] bg-white">
      <CardContent className="flex flex-col items-center gap-4 px-6 py-10 text-center">
        <div className="rounded-lg bg-[#0b8a67]/10 p-3 text-[#0b8a67]">
          <Newspaper className="h-6 w-6" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-black text-[#111]">No BUZZ items in this view yet</h2>
          <p className="max-w-xl text-sm font-medium text-[#6f6a5f]">
            Switch back to the broader feed to bring stories back into view.
          </p>
        </div>
        <Button onClick={onReset}>Back to For you</Button>
      </CardContent>
    </Card>
  );
}

function FilterPill({
  label,
  selected,
  icon,
  onClick,
}: {
  label: string;
  selected: boolean;
  icon?: ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        selected
          ? 'inline-flex items-center gap-1.5 rounded-full border border-[#111] bg-[#111] px-3 py-1.5 text-sm font-bold text-white'
          : 'inline-flex items-center gap-1.5 rounded-full border border-[#ded8ca] bg-white px-3 py-1.5 text-sm font-bold text-[#6f6a5f] transition-colors hover:border-[#111] hover:text-[#111]'
      }
    >
      {icon}
      {label}
    </button>
  );
}

function BuzzLiveTicker({
  pulseBlock,
  liveBlock,
  updatedAt,
  isRefreshing,
  onOpenItem,
}: {
  pulseBlock: BuzzFeedV1Block | null;
  liveBlock: BuzzFeedV1Block | null;
  updatedAt: string | null;
  isRefreshing: boolean;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const liveItems = liveBlock?.items ?? [];
  const pulseItems = pulseBlock?.items ?? [];
  const items = [...liveItems, ...pulseItems].slice(0, 12);

  if (items.length === 0) {
    return null;
  }

  const marqueeItems = items.length > 1 ? [...items, ...items] : items;

  return (
    <div className="border-b border-black bg-[#101010] text-white">
      <div className="flex h-11 items-center overflow-hidden">
        <div className="flex h-full shrink-0 items-center gap-2 bg-[#d51f2a] px-4 text-[11px] font-black uppercase tracking-[0.16em]">
          <span className="h-2 w-2 rounded-full bg-white shadow-[0_0_0_4px_rgba(255,255,255,0.22)]" />
          Live
        </div>

        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex w-max items-center gap-6 px-4 text-sm animate-[buzzTickerScroll_42s_linear_infinite]">
            {marqueeItems.map((item, index) => (
              <button
                key={`${item.itemId || item.headline || 'ticker'}-${index}`}
                type="button"
                onClick={() => onOpenItem(item)}
                className="inline-flex max-w-[420px] items-center gap-2 whitespace-nowrap text-left text-white/88 transition-colors hover:text-white"
              >
                <span className="text-[10px] font-black uppercase tracking-[0.16em] text-white/45">
                  {getTickerLabel(item)}
                </span>
                <span className="truncate font-semibold">{getHeadline(item)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="hidden h-full shrink-0 items-center gap-2 border-l border-white/10 px-4 text-[11px] font-bold uppercase tracking-[0.14em] text-white/55 sm:flex">
          {isRefreshing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : null}
          {updatedAt ? formatRelativeTime(updatedAt) : 'Now'}
        </div>
      </div>
      <style>{`
        @keyframes buzzTickerScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

function BuzzRightRail({
  pulseBlock,
  liveBlock,
  bodyBlocks,
  updatedAt,
  showAds,
  onOpenItem,
}: {
  pulseBlock: BuzzFeedV1Block | null;
  liveBlock: BuzzFeedV1Block | null;
  bodyBlocks: BuzzFeedV1Block[];
  updatedAt: string | null;
  showAds: boolean;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const liveItems = (liveBlock?.items ?? []).slice(0, 3);
  const pulseItems = (pulseBlock?.items ?? []).slice(0, 4);
  const rankingBlocks = bodyBlocks
    .filter((block) => block.blockType === 'RANKINGS_BLOCK' && (block.items?.length ?? 0) > 0)
    .map((block) => ({
      ...block,
      items: (block.items ?? []).slice(0, 10),
    }));
  const quickReadItems = (bodyBlocks.find((block) => block.blockType === 'NEWS_BLOCK')?.items ?? []).slice(0, 4);

  if (!liveItems.length && !pulseItems.length && !rankingBlocks.length && !quickReadItems.length) {
    return null;
  }

  return (
    <aside className="space-y-4 xl:sticky xl:top-4 xl:self-start">
      <div className="rounded-lg border border-[#dfd9ca] bg-white px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#9a9487]">Today</p>
            <p className="mt-1 text-xl font-black text-[#111]">Pulse</p>
          </div>
          <span className="rounded-full bg-[#f2efe6] px-3 py-1 text-xs font-bold text-[#6f6a5f]">
            {updatedAt ? formatRelativeTime(updatedAt) : 'Now'}
          </span>
        </div>
      </div>

      {liveItems.length ? (
        <BuzzRailPanel title="Live courts" meta={`${liveItems.length} active`}>
          <div className="space-y-2.5">
            {liveItems.map((item, index) => (
              <BuzzMiniLiveCard
                key={item.itemId || item.headline || index}
                item={item}
                onOpenItem={onOpenItem}
              />
            ))}
          </div>
        </BuzzRailPanel>
      ) : null}

      {pulseItems.length ? (
        <BuzzRailPanel title="Trending" icon={<Flame className="h-4 w-4" />}>
          <div className="divide-y divide-[#ece7da]">
            {pulseItems.map((item, index) => (
              <BuzzRailStory
                key={item.itemId || item.headline || index}
                item={item}
                index={index}
                onOpenItem={onOpenItem}
              />
            ))}
          </div>
        </BuzzRailPanel>
      ) : null}

      {rankingBlocks.length ? (
        <div className="space-y-4">
          {rankingBlocks.map((block, index) => (
            <BuzzRailRankingPanel
              key={`${block.blockId || block.title || 'ranking'}-${index}`}
              block={block}
              onOpenItem={onOpenItem}
            />
          ))}
        </div>
      ) : null}

      {quickReadItems.length ? (
        <BuzzRailPanel title="Quick reads">
          <div className="divide-y divide-[#ece7da]">
            {quickReadItems.map((item, index) => (
              <BuzzRailStory
                key={item.itemId || item.headline || index}
                item={item}
                index={index}
                onOpenItem={onOpenItem}
              />
            ))}
          </div>
        </BuzzRailPanel>
      ) : null}

      {showAds && quickReadItems.length ? <BuzzRightRailAdStack count={2} /> : null}
    </aside>
  );
}

function BuzzRailRankingPanel({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = (block.items ?? []).slice(0, 10);
  if (!items.length) {
    return null;
  }

  const title = normalizeRankingTitle(block.title) || 'Rankings';
  const meta = title.toLowerCase().includes('top 10') ? undefined : 'Top 10';

  return (
    <BuzzRailPanel title={title} meta={meta}>
      <div className="divide-y divide-[#ece7da]">
        {items.map((item, index) => {
          const movementLabel = getRailRankingMovementDisplay(item);
          const headshotUrl = getHeadshotUrl(item);

          return (
            <button
              key={item.itemId || item.headline || index}
              type="button"
              onClick={() => onOpenItem(item)}
              className="grid w-full grid-cols-[28px_38px_minmax(0,1fr)_38px] items-center gap-2 py-2.5 text-left first:pt-0 last:pb-0"
            >
              <span className="text-center text-sm font-black tabular-nums text-[#0b8a67]">{formatRankForRail(item, index)}</span>
              <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-[#ece7da] bg-[#f2efe6] text-[#0b8a67]">
                {headshotUrl ? (
                  <img src={headshotUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <Users className="h-4 w-4" />
                )}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-black leading-5 text-[#111]">{getHeadline(item)}</span>
                <span className="mt-0.5 block truncate text-[11px] font-semibold text-[#7a7468]">
                  {[getRankingCountry(item), getRankingPointsLabel(item)].filter(Boolean).join(' • ')}
                </span>
              </span>
              <span className="flex justify-end">
                {movementLabel ? <span className={getRailRankingMovementClassName(item)}>{movementLabel}</span> : null}
              </span>
            </button>
          );
        })}
      </div>
    </BuzzRailPanel>
  );
}

function BuzzRightRailAdStack({ count }: { count: number }) {
  const slots = Array.from({ length: Math.max(1, Math.min(3, count)) });

  return (
    <div className="space-y-3">
      {slots.map((_, index) => (
        <BuzzInFeedAdCard key={`right-rail-ad-${index}`} slotIndex={100 + index} />
      ))}
    </div>
  );
}

function BuzzRailPanel({
  title,
  meta,
  icon,
  children,
}: {
  title: string;
  meta?: string;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-[#dfd9ca] bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {icon ? <span className="text-[#0b8a67]">{icon}</span> : null}
          <h2 className="text-[11px] font-black uppercase tracking-[0.18em] text-[#8f897c]">{title}</h2>
        </div>
        {meta ? <p className="text-xs font-bold text-[#7a7468]">{meta}</p> : null}
      </div>
      {children}
    </section>
  );
}

function BuzzRailStory({
  item,
  index,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  index: number;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="flex w-full gap-3 py-3 text-left first:pt-0 last:pb-0"
    >
      <span className="mt-0.5 w-5 shrink-0 text-sm font-black text-[#0b8a67]">{index + 1}</span>
      <span className="min-w-0 flex-1">
        <span className="block line-clamp-2 text-sm font-black leading-5 text-[#111]">{getHeadline(item)}</span>
        <span className="mt-1 block text-xs font-semibold text-[#7a7468]">
          {[getSourceLabel(item), formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))].filter(Boolean).join(' • ')}
        </span>
      </span>
    </button>
  );
}

function BuzzMiniLiveCard({
  item,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const setScores = getLiveStructuredSetScores(item).slice(-3);
  const pointPair = getLivePointPair(item);
  const participants = getParticipants(item).slice(0, 2);

  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="w-full rounded-lg bg-[#111] p-3 text-left text-white transition-colors hover:bg-[#1d1d1b]"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <p className="truncate text-[11px] font-black uppercase tracking-[0.14em] text-white/62">
          {getLiveCompetitionLabel(item)}
        </p>
        <span className="rounded-full bg-[#d51f2a] px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.14em] text-white">
          Live
        </span>
      </div>

      <div className="mb-2 flex items-center">
        <div className="min-w-0 flex-1" />
        {setScores.map((setScore) => (
          <span key={setScore.setOrder} className="w-6 text-center text-[10px] font-bold text-white/45">
            {setScore.setOrder}
          </span>
        ))}
        <span className="w-8 text-center text-[10px] font-black text-white/45">Pts</span>
      </div>

      <div className="space-y-1.5">
        {participants.map((participant, participantIndex) => (
          <div key={`${getParticipantName(participant)}-${participantIndex}`} className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span
                className={
                  isParticipantServing(participant)
                    ? 'h-2 w-2 rounded-full bg-[#0b8a67] shadow-[0_0_0_3px_rgba(11,138,103,0.22)]'
                    : 'h-2 w-2 rounded-full bg-transparent'
                }
              />
              <p className="truncate text-xs font-black text-white">{getCompactParticipantName(participant)}</p>
            </div>
            {setScores.map((setScore) => (
              <span key={`${participantIndex}-${setScore.setOrder}`} className={getLiveSetClassName(setScore)}>
                {getLiveSetDisplayValue(setScore, participantIndex === 0 ? 'left' : 'right')}
              </span>
            ))}
            <span
              className={
                participantIndex === 0
                  ? getLivePointClassName(pointPair?.left, pointPair)
                  : getLivePointClassName(pointPair?.right, pointPair)
              }
            >
              {participantIndex === 0 ? pointPair?.left || '-' : pointPair?.right || '-'}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-2 truncate text-[10px] font-bold uppercase tracking-[0.12em] text-white/50">{getLiveFooter(item)}</p>
    </button>
  );
}

function BuzzHeroBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block | null;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const item = block?.items?.[0];
  if (!item) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="group relative min-h-[430px] w-full overflow-hidden rounded-lg bg-[#111] text-left shadow-[0_18px_45px_-28px_rgba(0,0,0,0.75)] sm:min-h-[520px]"
    >
      {getImageUrl(item) ? (
        <img
          src={getImageUrl(item) ?? ''}
          alt=""
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1b1b19] text-white/70">
          <Newspaper className="h-12 w-12" />
        </div>
      )}

      <div className="absolute inset-0 bg-gradient-to-b from-black/8 via-black/22 to-black/92" />
      <div className="relative flex min-h-[430px] flex-col justify-end p-5 sm:min-h-[520px] sm:p-7 lg:p-8">
        <div className="max-w-4xl">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-3 py-1 text-[11px] font-black uppercase tracking-[0.14em] text-[#111]">
              {getReasonLabel(item) || 'Featured'}
            </span>
            <span className="text-[11px] font-black uppercase tracking-[0.18em] text-white/70">{getSourceLabel(item)}</span>
            {getSportLabel(item) ? (
              <span className="text-[11px] font-black uppercase tracking-[0.18em] text-[#f0c35a]">{getSportLabel(item)}</span>
            ) : null}
          </div>

          <h2 className="mt-4 max-w-4xl text-[34px] font-black leading-[0.98] text-white sm:text-[52px] lg:text-[64px]">
            {getHeadline(item)}
          </h2>
          {getSummary(item) ? (
            <p className="mt-4 max-w-2xl text-base font-medium leading-7 text-white/76">{getSummary(item)}</p>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs font-black uppercase tracking-[0.14em] text-white/62">
            <span>{formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))}</span>
            <span className="inline-flex items-center gap-2 text-white transition-transform group-hover:translate-x-0.5">
              Open story
              <ArrowRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function BuzzPageBodyBlock({
  block,
  onOpenItem,
  showAds,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
  showAds: boolean;
}) {
  switch (block.blockType) {
    case 'RESULTS_TICKER_BLOCK':
      return <BuzzResultsBlock block={block} onOpenItem={onOpenItem} />;
    case 'NEWS_BLOCK':
      return <BuzzNewsBlock block={block} onOpenItem={onOpenItem} showAds={showAds} />;
    case 'VIDEO_CAROUSEL_BLOCK':
    case 'LESSONS_BLOCK':
      return <BuzzMediaBlock block={block} onOpenItem={onOpenItem} />;
    case 'RANKINGS_BLOCK':
      return <BuzzRankingsBlock block={block} onOpenItem={onOpenItem} />;
    case 'SPONSORED_BLOCK':
      return <BuzzSponsoredBlock block={block} onOpenItem={onOpenItem} />;
    case 'CTA_BLOCK':
      return <BuzzCtaBlock block={block} onOpenItem={onOpenItem} />;
    default:
      return <BuzzGenericBlock block={block} onOpenItem={onOpenItem} />;
  }
}

function BuzzResultsBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-[#dfd9ca] bg-white p-4">
      <SectionHeading title={block.title || 'Results'} meta={`${items.length} updates`} />
      <div className="-mx-4 mt-3 overflow-x-auto px-4 pb-1">
        <div className="flex gap-3">
          {items.map((item, index) => (
            <button
              key={item.itemId || item.headline || index}
              type="button"
              onClick={() => onOpenItem(item)}
              className="w-[238px] shrink-0 rounded-lg border border-[#ece7da] bg-[#fbfaf4] p-4 text-left transition-colors hover:border-[#111]"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#8f897c]">{getSportLabel(item) || 'Score'}</p>
              <p className="mt-2 line-clamp-2 text-[15px] font-black leading-5 text-[#111]">{getHeadline(item)}</p>
              <p className="mt-4 text-2xl font-black text-[#0b8a67]">{getScoreSummary(item) || 'Posted'}</p>
              <p className="mt-2 text-xs font-semibold text-[#7a7468]">
                {[getScoreStatus(item), formatDateTimeLabel(getPublishedAt(item))].filter(Boolean).join(' • ')}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function BuzzNewsBlock({
  block,
  onOpenItem,
  showAds,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
  showAds: boolean;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  const mixedItems = createMixedBuzzFeed(items, {
    enableAds: showAds,
  });
  const isForYouBlock = isForYouNewsBlock(block);
  const { leadArticles, remainder } = splitLeadNewsEntries(mixedItems, isForYouBlock ? 3 : 2);
  const leadEntry = leadArticles[0] ?? null;
  const sideEntries = leadArticles.slice(1);

  return (
    <section className="space-y-4">
      <SectionHeading title={block.title || 'Top stories'} meta={`${items.length} stories`} />

      {leadEntry ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.18fr)_minmax(280px,0.82fr)]">
          <BuzzFeatureStoryCard item={leadEntry.item} onOpenItem={onOpenItem} />
          {sideEntries.length ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
              {sideEntries.map((entry) => (
                <BuzzSideStoryCard key={entry.id} item={entry.item} onOpenItem={onOpenItem} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className={isForYouBlock ? 'grid gap-3 md:grid-cols-2 2xl:grid-cols-3' : 'space-y-3'}>
        {remainder.map((entry) => {
          if (entry.kind === 'AD') {
            return <BuzzInFeedAdCard key={entry.id} slotIndex={entry.slotIndex} />;
          }

          const item = entry.item;
          return isForYouBlock ? (
            <BuzzGridStoryCard key={entry.id} item={item} onOpenItem={onOpenItem} />
          ) : (
            <BuzzCompactStoryRow key={entry.id} item={item} onOpenItem={onOpenItem} />
          );
        })}
      </div>
    </section>
  );
}

function BuzzFeatureStoryCard({
  item,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="group overflow-hidden rounded-lg border border-[#dfd9ca] bg-white text-left transition-colors hover:border-[#111]"
    >
      <BuzzStoryImage item={item} className="h-[300px] sm:h-[380px] 2xl:h-[440px]" />
      <div className="p-4 sm:p-5">
        <BuzzStoryMeta item={item} />
        <h3 className="mt-3 line-clamp-3 text-[26px] font-black leading-[1.05] text-[#111] sm:text-[34px]">
          {getHeadline(item)}
        </h3>
        {getSummary(item) ? (
          <p className="mt-3 line-clamp-2 text-sm font-medium leading-6 text-[#6f6a5f]">{getSummary(item)}</p>
        ) : null}
      </div>
    </button>
  );
}

function BuzzSideStoryCard({
  item,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="group overflow-hidden rounded-lg border border-[#dfd9ca] bg-white text-left transition-colors hover:border-[#111]"
    >
      <BuzzStoryImage item={item} className="h-[190px] sm:h-[210px] lg:h-[190px] 2xl:h-[218px]" />
      <div className="p-4">
        <BuzzStoryMeta item={item} />
        <h3 className="mt-2 line-clamp-3 text-xl font-black leading-[1.12] text-[#111]">{getHeadline(item)}</h3>
      </div>
    </button>
  );
}

function BuzzGridStoryCard({
  item,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="group overflow-hidden rounded-lg border border-[#dfd9ca] bg-white text-left transition-colors hover:border-[#111]"
    >
      <BuzzStoryImage item={item} className="h-[210px] sm:h-[240px] xl:h-[260px]" />
      <div className="p-4">
        <BuzzStoryMeta item={item} />
        <h3 className="mt-2 line-clamp-3 text-lg font-black leading-[1.16] text-[#111]">{getHeadline(item)}</h3>
      </div>
    </button>
  );
}

function BuzzCompactStoryRow({
  item,
  onOpenItem,
}: {
  item: BuzzFeedV1Item;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpenItem(item)}
      className="group flex w-full items-center gap-4 rounded-lg border border-[#dfd9ca] bg-white p-3 text-left transition-colors hover:border-[#111]"
    >
      <BuzzStoryImage item={item} className="h-[118px] w-[132px] shrink-0 sm:h-[132px] sm:w-[164px]" />
      <div className="min-w-0 flex-1">
        <BuzzStoryMeta item={item} />
        <h3 className="mt-2 line-clamp-2 text-[17px] font-black leading-[1.2] text-[#111]">{getHeadline(item)}</h3>
        {getSummary(item) ? (
          <p className="mt-2 hidden line-clamp-2 text-sm font-medium leading-5 text-[#6f6a5f] sm:block">{getSummary(item)}</p>
        ) : null}
      </div>
      <ExternalLink className="hidden h-4 w-4 shrink-0 text-[#9a9487] sm:block" />
    </button>
  );
}

function BuzzStoryImage({
  item,
  className,
}: {
  item: BuzzFeedV1Item;
  className: string;
}) {
  const imageUrl = getImageUrl(item);
  if (!imageUrl) {
    return (
      <div className={`flex items-center justify-center bg-[#ece7da] text-[#0b8a67] ${className}`}>
        <Newspaper className="h-9 w-9" />
      </div>
    );
  }

  return (
    <div className={`overflow-hidden bg-[#ece7da] ${className}`}>
      <img
        src={imageUrl}
        alt=""
        className="h-full w-full object-cover object-[center_top] transition-transform duration-500 group-hover:scale-[1.03]"
        loading="lazy"
      />
    </div>
  );
}

function BuzzStoryMeta({ item }: { item: BuzzFeedV1Item }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-[#8f897c]">
      <span>{getSourceLabel(item)}</span>
      {getSportLabel(item) ? <span className="text-[#0b8a67]">{getSportLabel(item)}</span> : null}
      <span>{formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))}</span>
    </div>
  );
}

function BuzzMediaBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="overflow-hidden rounded-lg bg-[#111] p-4 text-white sm:p-5">
      <SectionHeading
        title={block.title || (block.blockType === 'LESSONS_BLOCK' ? 'Lessons' : 'Watch')}
        meta={`${items.length} clips`}
        tone="dark"
      />
      <div className="-mx-4 mt-4 overflow-x-auto px-4 pb-1">
        <div className="flex gap-3">
          {items.map((item, index) => {
            const durationChip = getDurationChipLabel(item);
            return (
              <button
                key={item.itemId || item.headline || index}
                type="button"
                onClick={() => onOpenItem(item)}
                className="group w-[260px] shrink-0 overflow-hidden rounded-lg border border-white/12 bg-white/8 text-left transition-colors hover:border-white/40"
              >
                <div className="relative">
                  {getImageUrl(item) ? (
                    <img
                      src={getImageUrl(item) ?? ''}
                      alt=""
                      className="h-40 w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-40 items-center justify-center bg-white/10 text-white">
                      <PlayCircle className="h-10 w-10" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-[#111] shadow-[0_16px_38px_-20px_rgba(0,0,0,0.9)]">
                      <PlayCircle className="h-6 w-6" />
                    </span>
                  </div>
                  {durationChip ? (
                    <span className="absolute bottom-3 right-3 rounded bg-black/70 px-2 py-1 text-[10px] font-black text-white">
                      {durationChip}
                    </span>
                  ) : null}
                </div>
                <div className="space-y-1.5 p-4">
                  <p className="line-clamp-2 text-[16px] font-black leading-5 text-white">{getHeadline(item)}</p>
                  <p className="text-xs font-semibold text-white/55">
                    {block.blockType === 'LESSONS_BLOCK' ? getLessonLabel(item) : getSourceLabel(item)}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuzzRankingsBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="rounded-lg border border-[#dfd9ca] bg-white p-4">
      <SectionHeading title={block.title || 'Rankings'} meta={`${items.length} players`} />
      <div className="mt-3 grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
        {items.map((item, index) => (
          <button
            key={item.itemId || item.headline || index}
            type="button"
            onClick={() => onOpenItem(item)}
            className="flex items-center gap-3 rounded-lg border border-[#ece7da] bg-[#fbfaf4] p-3 text-left transition-colors hover:border-[#111]"
          >
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-[#111]">
              {getHeadshotUrl(item) ? (
                <img src={getHeadshotUrl(item) ?? ''} alt="" className="h-full w-full object-cover" loading="lazy" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-white/70">
                  <Users className="h-6 w-6" />
                </div>
              )}
              <div className="absolute left-1 top-1 rounded bg-white px-1.5 py-0.5 text-[10px] font-black text-[#111]">{getRank(item)}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-[15px] font-black text-[#111]">{getHeadline(item)}</p>
                  <p className="mt-1 text-[11px] font-semibold text-[#7a7468]">
                    {[getRankingCountry(item), getRankingLabel(item)].filter(Boolean).join(' • ')}
                  </p>
                  <p className="mt-1 text-[11px] font-black text-[#0b8a67]">{getRankingPointsLabel(item)}</p>
                </div>
                <p className={getRailRankingMovementClassName(item)}>{getRankingMovementDisplay(item) || ' '}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function BuzzSponsoredBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const item = block.items?.[0];
  if (!item) {
    return null;
  }

  return (
    <section className="space-y-3">
      <SectionHeading title={block.title || 'Partner spotlight'} meta="Sponsored" />
      <button
        type="button"
        onClick={() => onOpenItem(item)}
        className="group grid w-full overflow-hidden rounded-lg border border-[#dfc676] bg-[#fff8df] text-left transition-colors hover:border-[#111] md:grid-cols-[0.82fr_1fr]"
      >
        <BuzzStoryImage item={item} className="h-56 md:h-full" />
        <div className="flex min-h-[230px] flex-col justify-between p-5">
          <span className="inline-flex w-fit rounded bg-[#111] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.16em] text-white">
            Sponsored
          </span>
          <div className="mt-4 space-y-3">
            <p className="text-[26px] font-black leading-[1.05] text-[#111]">{getHeadline(item)}</p>
            {getSummary(item) ? <p className="max-w-3xl text-sm font-medium leading-6 text-[#6f6a5f]">{getSummary(item)}</p> : null}
          </div>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-[#0b8a67]">
            Open
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </button>
    </section>
  );
}

function BuzzCtaBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <SectionHeading title={block.title || 'BUZZ'} />
      <div className="grid gap-4 md:grid-cols-2">
        {items.map((item, index) => (
          <button
            key={item.itemId || item.headline || index}
            type="button"
            onClick={() => onOpenItem(item)}
            className={
              index % 2 === 0
                ? 'flex min-h-[180px] flex-col justify-between rounded-lg bg-[#0b8a67] p-5 text-left text-white'
                : 'flex min-h-[180px] flex-col justify-between rounded-lg bg-[#111] p-5 text-left text-white'
            }
          >
            <div className="space-y-3">
              <p className="text-[24px] font-black leading-tight">{getHeadline(item)}</p>
              {getSummary(item) ? <p className="text-sm leading-6 text-white/80">{getSummary(item)}</p> : null}
            </div>
            <span className="inline-flex items-center gap-2 text-sm font-black">
              Open
              <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function BuzzGenericBlock({
  block,
  onOpenItem,
}: {
  block: BuzzFeedV1Block;
  onOpenItem: (item: BuzzFeedV1Item) => void;
}) {
  const items = block.items ?? [];
  if (items.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <SectionHeading title={block.title || 'More stories'} meta={`${items.length} items`} />
      <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-3">
        {items.map((item, index) => (
          <button
            key={item.itemId || item.headline || index}
            type="button"
            onClick={() => onOpenItem(item)}
            className="rounded-lg border border-[#dfd9ca] bg-white p-4 text-left transition-colors hover:border-[#111]"
          >
            <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#8f897c]">{getSourceLabel(item)}</p>
            <p className="mt-2 line-clamp-2 text-[17px] font-black leading-6 text-[#111]">{getHeadline(item)}</p>
            <p className="mt-2 text-xs font-semibold text-[#7a7468]">
              {[getReasonLabel(item), formatDateTimeLabel(getPublishedAt(item) || getUpdatedAt(item))].filter(Boolean).join(' • ')}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

function SectionHeading({
  title,
  meta,
  tone = 'light',
}: {
  title: string;
  meta?: string;
  tone?: 'light' | 'dark';
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <h2
        className={
          tone === 'dark'
            ? 'text-[11px] font-black uppercase tracking-[0.18em] text-white/56'
            : 'text-[11px] font-black uppercase tracking-[0.18em] text-[#8f897c]'
        }
      >
        {title}
      </h2>
      {meta ? (
        <p className={tone === 'dark' ? 'text-xs font-bold text-white/50' : 'text-xs font-bold text-[#7a7468]'}>{meta}</p>
      ) : null}
    </div>
  );
}

function BuzzInFeedAdCard({ slotIndex }: { slotIndex: number }) {
  const adRef = useRef<HTMLModElement | null>(null);
  const hostMode = getBuzzAdHostMode();
  const adConfig = getBuzzAdSenseConfig();
  const [privacyReady, setPrivacyReady] = useState(hostMode === 'test');

  useEffect(() => {
    if (hostMode !== 'production') {
      return;
    }

    let cancelled = false;
    void prepareBuzzAdSense().then((ready) => {
      if (!cancelled) {
        setPrivacyReady(ready);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [hostMode]);

  useEffect(() => {
    if (hostMode !== 'production' || !privacyReady || !adRef.current) {
      return;
    }

    try {
      if (adRef.current.getAttribute('data-adsbygoogle-status') === 'done') {
        return;
      }
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (error) {
      console.error('Failed to initialize BUZZ AdSense slot', error);
    }
  }, [hostMode, privacyReady, slotIndex]);

  if (!isBuzzWebAdsEnabled()) {
    return null;
  }

  if (hostMode === 'test') {
    return <BuzzPreviewInFeedAdCard slotIndex={slotIndex} />;
  }

  if (!privacyReady) {
    return null;
  }

  return (
    <div className="min-h-[190px] overflow-hidden rounded-lg border border-[#dfd9ca] bg-white px-3 py-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#8f897c]">Sponsored</p>
        <p className="text-[10px] font-semibold text-[#7a7468]">Ad</p>
      </div>
      <ins
        ref={adRef}
        className="adsbygoogle block"
        style={{ display: 'block', minHeight: '150px' }}
        data-ad-client={adConfig.clientId}
        data-ad-slot={adConfig.slotId}
        data-ad-format="fluid"
        data-ad-layout-key={adConfig.layoutKey}
      />
    </div>
  );
}

function BuzzPreviewInFeedAdCard({ slotIndex }: { slotIndex: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-dashed border-[#0b8a67]/40 bg-[#0b8a67]/5 px-3 py-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#0b8a67]">Sponsored</p>
        <p className="text-[10px] font-semibold text-[#7a7468]">Preview</p>
      </div>
      <div className="rounded-lg bg-white/80 p-4">
        <p className="text-[13px] font-black text-[#111]">BUZZ feed ad slot</p>
        <p className="mt-2 text-xs font-medium leading-5 text-[#6f6a5f]">
          Test placement {slotIndex + 1}. Real AdSense loads only on the approved public app host.
        </p>
      </div>
    </div>
  );
}

function useDocumentVisibility() {
  const [visible, setVisible] = useState(() => {
    if (typeof document === 'undefined') {
      return true;
    }
    return document.visibilityState === 'visible';
  });

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityChange = () => {
      setVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  return visible;
}

function createMixedBuzzFeed(
  organicItems: BuzzFeedV1Item[],
  options: {
    adInterval?: number;
    enableAds?: boolean;
  } = {}
): BuzzListItem[] {
  const { adInterval = BUZZ_AD_INSERTION_INTERVAL, enableAds = false } = options;
  const mixed: BuzzListItem[] = [];
  let adSlotIndex = 0;

  organicItems.forEach((item, index) => {
    mixed.push({
      kind: 'ARTICLE',
      id: item.itemId || item.headline || `buzz-item-${index}`,
      item,
    });

    if (enableAds && adInterval > 0 && (index + 1) % adInterval === 0) {
      mixed.push({
        kind: 'AD',
        id: `slot:ad:${adSlotIndex}`,
        slotIndex: adSlotIndex,
      });
      adSlotIndex += 1;
    }
  });

  return mixed;
}

function splitLeadNewsEntries(entries: BuzzListItem[], maxLeadCount = 2): {
  leadArticles: Array<Extract<BuzzListItem, { kind: 'ARTICLE' }>>;
  remainder: BuzzListItem[];
} {
  const leadArticles: Array<Extract<BuzzListItem, { kind: 'ARTICLE' }>> = [];
  const remainder: BuzzListItem[] = [];
  let leadCount = 0;

  for (const entry of entries) {
    if (entry.kind === 'ARTICLE' && leadCount < maxLeadCount) {
      leadArticles.push(entry);
      leadCount += 1;
      continue;
    }
    remainder.push(entry);
  }

  return { leadArticles, remainder };
}

function isForYouNewsBlock(block: BuzzFeedV1Block) {
  const title = (block.title ?? '').trim().toLowerCase();
  return !title || title === 'for you';
}

function mergeBuzzTopSection(
  top: BuzzFeedV1Response['top'],
  liveRefresh: BuzzLiveRefreshResponse | null
): BuzzFeedV1Response['top'] {
  if (!top && !liveRefresh) {
    return null;
  }

  return {
    ...(top ?? {}),
    pulseStrip: liveRefresh?.pulseStrip ?? top?.pulseStrip ?? null,
    liveRail: liveRefresh?.liveRail ?? top?.liveRail ?? null,
    hero: top?.hero ?? null,
  };
}

function filterBlock(
  block: BuzzFeedV1Block | null | undefined,
  predicate: (item: BuzzFeedV1Item) => boolean,
  enabled: boolean
): BuzzFeedV1Block | null {
  if (!enabled || !block?.items?.length) {
    return null;
  }

  const items = block.items.filter(predicate);
  if (!items.length) {
    return null;
  }

  return {
    ...block,
    items,
  };
}

function shouldShowTop(kind: 'pulseStrip' | 'liveRail' | 'hero', view: BuzzViewFilter) {
  if (view === 'for_you' || view === 'local') {
    return true;
  }
  if (kind === 'hero') {
    return view === 'news';
  }
  return view === 'live' || view === 'results';
}

function shouldShowBody(block: BuzzFeedV1Block, view: BuzzViewFilter) {
  if (view === 'for_you' || view === 'local') {
    return true;
  }
  if (view === 'results' || view === 'live') {
    return block.blockType === 'RESULTS_TICKER_BLOCK';
  }
  if (view === 'news') {
    return block.blockType === 'NEWS_BLOCK';
  }
  if (view === 'videos') {
    return block.blockType === 'VIDEO_CAROUSEL_BLOCK';
  }
  if (view === 'lessons') {
    return block.blockType === 'LESSONS_BLOCK';
  }
  if (view === 'rankings') {
    return block.blockType === 'RANKINGS_BLOCK';
  }
  return true;
}

function resolveBuzzFeedItemUrl(item: BuzzFeedV1Item): string | null {
  const primaryUrl = item.actions?.primary?.url?.trim();
  if (primaryUrl) {
    return primaryUrl;
  }

  const secondary = item.actions?.secondary?.find((action) => action.url?.trim());
  return secondary?.url?.trim() || null;
}

function getHeadline(item: BuzzFeedV1Item) {
  return stringValue(item.headline) || 'BUZZ';
}

function getSourceLabel(item: BuzzFeedV1Item) {
  return stringValue(item.source?.sourceLabel) || 'TARK';
}

function getSummary(item: BuzzFeedV1Item) {
  return stringValue(item.summary);
}

function getPublishedAt(item: BuzzFeedV1Item) {
  return stringValue(item.publishedAt);
}

function getUpdatedAt(item: BuzzFeedV1Item) {
  return stringValue(item.updatedAt);
}

function getReasonLabel(item: BuzzFeedV1Item) {
  return stringValue(item.reason?.label);
}

function getSportLabel(item: BuzzFeedV1Item) {
  return stringValue(item.sport)?.replace(/_/g, ' ');
}

function getAttachmentImageUrl(item: BuzzFeedV1Item) {
  const attachments = (item as Record<string, unknown>).attachments;
  if (!Array.isArray(attachments)) {
    return undefined;
  }
  for (const attachment of attachments) {
    if (!attachment || typeof attachment !== 'object') {
      continue;
    }
    const record = attachment as Record<string, unknown>;
    const url = firstAllowedImageUrl(
      stringValue(record['thumbnailUrl']),
      stringValue(record['imageUrl']),
      stringValue(record['url']),
    );
    if (url) {
      return url;
    }
  }
  return undefined;
}

function getImageUrl(item: BuzzFeedV1Item) {
  const media = item.media as Record<string, unknown> | null | undefined;
  return firstAllowedImageUrl(
    stringValue(item.media?.thumbnailUrl),
    stringValue(media?.['imageUrl']),
    stringValue(media?.['posterUrl']),
    stringValue(item.thumbnailUrl),
    stringValue(item.imageUrl),
    stringValue(item.image),
    stringValue(item.imageFallback),
    getAttachmentImageUrl(item),
  );
}

function getPulseEyebrow(item: BuzzFeedV1Item) {
  return stringValue(item.itemType)?.replace(/_/g, ' ') || 'NOW';
}

function getPulseFootnote(item: BuzzFeedV1Item) {
  return getScoreStatus(item) || getReasonLabel(item) || getSourceLabel(item);
}

function isLiveBuzzItem(item: BuzzFeedV1Item) {
  return (stringValue(item.status) || '').toUpperCase() === 'LIVE' || getPulseFootnote(item).toUpperCase().includes('LIVE');
}

function getTickerLabel(item: BuzzFeedV1Item) {
  if (isLiveBuzzItem(item)) {
    return 'Live';
  }
  return getPulseEyebrow(item);
}

function getLiveCompetitionLabel(item: BuzzFeedV1Item) {
  return (
    firstString(
      stringValue(item.event?.competition),
      stringValue(item.event?.title),
      getEventLabel(item),
      'Live Match'
    ) || 'Live Match'
  );
}

function getLiveStageLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.event?.stage),
    getFallbackLiveStage(stringValue(item.event?.title), stringValue(item.event?.competition))
  );
}

function getParticipants(item: BuzzFeedV1Item) {
  return Array.isArray(item.participants) ? item.participants : [];
}

function getEventLabel(item: BuzzFeedV1Item) {
  return firstString(stringValue(item.event?.title), stringValue(item.event?.competition), getSourceLabel(item));
}

function getParticipantName(participant: unknown) {
  if (!participant || typeof participant !== 'object') {
    return 'Player';
  }

  const record = participant as Record<string, unknown>;
  return (
    firstString(
      stringValue(record['displayName']),
      stringValue(record['shortName']),
      stringValue(record['name']),
      [stringValue(record['firstName']), stringValue(record['lastName'])].filter(Boolean).join(' ').trim() || null
    ) || 'Player'
  );
}

function getCompactParticipantName(participant: unknown) {
  return compactParticipantName(getParticipantName(participant));
}

function compactParticipantName(name: string) {
  if (!name.includes(' ')) {
    return name;
  }

  const last = lastToken(name);
  const firstInitial = name.trim().charAt(0);
  return firstInitial ? `${firstInitial}. ${last}` : last;
}

function lastToken(value: string) {
  const tokens = value.trim().split(/\s+/);
  return tokens[tokens.length - 1] || value;
}

function isParticipantServing(participant: unknown) {
  if (!participant || typeof participant !== 'object') {
    return false;
  }

  const record = participant as Record<string, unknown>;
  return Boolean(record.isServing);
}

function getScoreSummary(item: BuzzFeedV1Item) {
  const scoreSummary = stringValue(item.score?.summaryText);
  if (scoreSummary) {
    return scoreSummary;
  }

  const scores = Array.isArray(item.scores) ? item.scores : [];
  if (!scores.length) {
    return '';
  }

  return scores
    .map((score) => {
      const left = numberValue(score.home_score);
      const right = numberValue(score.away_score);
      if (left == null || right == null) {
        return null;
      }
      return `${left}-${right}`;
    })
    .filter(Boolean)
    .join(' ');
}

function getScoreStatus(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.status),
    stringValue(item.score?.statusText),
    stringValue(item.event?.stage)
  );
}

type StructuredSetScore = {
  setOrder: number;
  leftGames: string;
  rightGames: string;
  leftTiebreak: string | null;
  rightTiebreak: string | null;
  isCompleted: boolean;
  isCurrent: boolean;
};

function getLiveStructuredSetScores(item: BuzzFeedV1Item): StructuredSetScore[] {
  const pointByPointSets = getLatestPointByPointSetOrder(item);
  const parsedSets = (Array.isArray(item.scores) ? item.scores : [])
    .map((score, index) => parseLiveSetScore(score, index + 1))
    .filter((score): score is StructuredSetScore => Boolean(score));

  if (parsedSets.length > 0) {
    return parsedSets;
  }

  return parseSummarySetScores(item, pointByPointSets);
}

function parseLiveSetScore(score: unknown, fallbackOrder: number): StructuredSetScore | null {
  if (!score || typeof score !== 'object') {
    return null;
  }

  const record = score as Record<string, unknown>;
  const setOrder = numberValue(record.score_set) ?? fallbackOrder;
  const leftGames = stringValue(record.home_score);
  const rightGames = stringValue(record.away_score);

  if (!leftGames || !rightGames) {
    return null;
  }

  return {
    setOrder,
    leftGames,
    rightGames,
    leftTiebreak: stringValue(record.score_first),
    rightTiebreak: stringValue(record.score_second),
    isCompleted: isCompletedLiveSet(leftGames, rightGames),
    isCurrent: false,
  };
}

function parseSummarySetScores(item: BuzzFeedV1Item, preferredCurrentSetOrder: number | null): StructuredSetScore[] {
  const summary = getScoreSummary(item);
  if (!summary) {
    return [];
  }

  const parsed: StructuredSetScore[] = [];
  summary
    .split(/\s+/)
    .forEach((segment, index) => {
      const [leftGames, rightGames] = segment.split('-');
      if (!leftGames || !rightGames) {
        return;
      }

      const setOrder = index + 1;
      parsed.push({
        setOrder,
        leftGames,
        rightGames,
        leftTiebreak: null,
        rightTiebreak: null,
        isCompleted: isCompletedLiveSet(leftGames, rightGames),
        isCurrent: resolveCurrentSetOrder(preferredCurrentSetOrder, index + 1, summary.split(/\s+/).length) === setOrder,
      });
    });

  return parsed;
}

function resolveCurrentSetOrder(preferred: number | null, fallback: number, totalSets: number) {
  if (preferred != null) {
    return preferred;
  }

  return totalSets > 0 ? totalSets : fallback;
}

function getLatestPointByPointSetOrder(item: BuzzFeedV1Item): number | null {
  const entries = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  const latestEntry = entries[entries.length - 1];
  if (!latestEntry) {
    return null;
  }
  return normalizeSetLabel(stringValue(latestEntry.set_number));
}

function isCompletedLiveSet(leftGames: string, rightGames: string) {
  const left = Number(leftGames);
  const right = Number(rightGames);
  if (!Number.isFinite(left) || !Number.isFinite(right)) {
    return false;
  }

  return (left >= 6 || right >= 6) && Math.abs(left - right) >= 2;
}

function getLiveSetDisplayValue(setScore: StructuredSetScore, side: 'left' | 'right') {
  const games = side === 'left' ? setScore.leftGames : setScore.rightGames;
  const tiebreak = side === 'left' ? setScore.leftTiebreak : setScore.rightTiebreak;
  return tiebreak ? `${games}(${tiebreak})` : games;
}

function getLiveSetClassName(setScore: StructuredSetScore) {
  if (setScore.isCurrent) {
    return 'w-6 text-center text-[12px] font-black text-white';
  }
  if (setScore.isCompleted) {
    return 'w-6 text-center text-[12px] font-bold text-white/70';
  }
  return 'w-6 text-center text-[12px] font-semibold text-white/45';
}

function getLivePointPair(item: BuzzFeedV1Item) {
  const point = getLatestLivePoint(item);
  const pointScore = point ? stringValue((point as Record<string, unknown>)['pointScore']) : null;
  if (pointScore) {
    return parseLivePointPair(pointScore);
  }

  return parseLivePointPair(stringValue(item.score?.currentGameScore));
}

function getLivePointClassName(value: string | undefined, pair: { left: string; right: string } | null) {
  const isActive = Boolean(value && pair && value !== '-' && (value !== pair.left || value !== pair.right));
  return isActive
    ? 'w-8 text-center text-[14px] font-black text-[#f0c35a]'
    : 'w-8 text-center text-[14px] font-black text-white/70';
}

function getLiveFooter(item: BuzzFeedV1Item) {
  return firstString(
    getCompactLiveStatus(item),
    getLiveStageLabel(item),
    getEventLabel(item),
    getLocationLabel(item),
    formatDateTimeLabel(getUpdatedAt(item) || getPublishedAt(item))
  ) || 'Live';
}

function getLatestPointByPointGame(item: BuzzFeedV1Item) {
  const entries = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  const latestEntry = entries[entries.length - 1];
  if (!latestEntry) {
    return null;
  }
  return normalizeGameLabel(stringValue(latestEntry.number_game));
}

function getLatestLivePoint(item: BuzzFeedV1Item) {
  const points = Array.isArray(item.pointByPoint) ? item.pointByPoint : [];
  if (!points.length) {
    return null;
  }
  const latestEntry = points[points.length - 1];
  const pointFrames = Array.isArray(latestEntry.points) ? latestEntry.points : [];
  return pointFrames[pointFrames.length - 1] ?? latestEntry;
}

function parseLivePointPair(pointScore?: string | null) {
  const normalized = normalizeLivePointString(pointScore);
  if (!normalized) {
    return null;
  }

  const [left, right] = normalized.split('-').map((value) => normalizePointValue(value));
  if (!left || !right) {
    return null;
  }

  return { left, right };
}

function normalizeLivePointString(value?: string | null) {
  const normalized = stringValue(value)?.replace(/\s+/g, '').replace(/:/g, '-');
  return normalized || null;
}

function normalizePointValue(value?: string | null) {
  const normalized = stringValue(value);
  if (!normalized) {
    return '-';
  }

  const upper = normalized.toUpperCase();
  if (upper === 'AD') {
    return 'AD';
  }

  return normalized;
}

function getLocationLabel(item: BuzzFeedV1Item) {
  return stringValue(item.event?.locationLabel);
}

function getFallbackLiveStage(title?: string | null, competition?: string | null) {
  const text = `${title || ''} ${competition || ''}`.toLowerCase();
  if (text.includes('quarter')) return 'Quarterfinal';
  if (text.includes('semi')) return 'Semifinal';
  if (text.includes('final')) return 'Final';
  if (text.includes('round of 16')) return 'Round of 16';
  return null;
}

function getCompactLiveStatus(item: BuzzFeedV1Item) {
  const gameLabel = getLatestPointByPointGame(item);
  const status = getScoreStatus(item);
  return firstString(gameLabel, status ? status.replace(/_/g, ' ') : null);
}

function normalizeSetLabel(value?: string | null) {
  if (!value) {
    return null;
  }

  const match = value.match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

function normalizeGameLabel(value?: string | null) {
  return value ? value.replace(/_/g, ' ') : null;
}

function getDurationChipLabel(item: BuzzFeedV1Item) {
  return formatDuration(numberValue(item.media?.durationSec));
}

function getLessonLabel(item: BuzzFeedV1Item) {
  return firstString(
    stringValue(item.lessonMeta?.level),
    stringValue(item.lessonMeta?.bestFor),
    getSourceLabel(item)
  );
}

function getRank(item: BuzzFeedV1Item) {
  return firstString(stringValue(item.rank), '#');
}

function formatRankForRail(item: BuzzFeedV1Item, index: number) {
  const rank = getRank(item);
  return rank === '#' ? String(index + 1) : rank;
}

function normalizeRankingTitle(value?: string | null) {
  const title = stringValue(value);
  if (!title) {
    return null;
  }
  return title.replace(/\s+rankings?$/i, '').replace(/\s+players?$/i, '').trim() || title;
}

function getRankingLabel(item: BuzzFeedV1Item) {
  return firstString(stringValue(item.rankingMeta?.tour), stringValue(item.rankingMeta?.rankingDate));
}

function getRankingPointsLabel(item: BuzzFeedV1Item) {
  const points = stringValue(item.rankingMeta?.points);
  return points ? `${points} pts` : '';
}

function getRankingMovementDisplay(item: BuzzFeedV1Item) {
  const movement = getRankingMovementValue(item);
  if (movement == null || movement === 0) {
    return '—';
  }
  if (movement > 0) {
    return `+${movement}`;
  }
  return `${movement}`;
}

function getRailRankingMovementDisplay(item: BuzzFeedV1Item) {
  const movement = getRankingMovementValue(item);
  if (movement == null || movement === 0) {
    return null;
  }
  return getRankingMovementDisplay(item);
}

function getRankingCountry(item: BuzzFeedV1Item) {
  return firstString(stringValue(item.playerMeta?.countryCode), stringValue(item.playerMeta?.country));
}

function getHeadshotUrl(item: BuzzFeedV1Item) {
  return firstAllowedImageUrl(stringValue(item.playerMeta?.headshotUrl), stringValue(item.media?.thumbnailUrl));
}

function getRailRankingMovementClassName(item: BuzzFeedV1Item) {
  const movement = getRankingMovementValue(item);
  if (movement == null || movement === 0) {
    return 'shrink-0 rounded bg-[#ece7da] px-2 py-1 text-[11px] font-black text-[#7a7468]';
  }
  if (movement > 0) {
    return 'shrink-0 rounded bg-emerald-100 px-2 py-1 text-[11px] font-black text-emerald-700';
  }
  return 'shrink-0 rounded bg-red-100 px-2 py-1 text-[11px] font-black text-red-700';
}

function getRankingMovementValue(item: BuzzFeedV1Item) {
  return numberValue(item.rankingMeta?.movementValue ?? item.rankingMeta?.movement);
}

function formatRelativeTime(value?: string | null) {
  if (!value) {
    return 'recently';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return 'recently';
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function formatDateTimeLabel(value?: string | null) {
  return value ? formatRelativeTime(value) : '';
}

function formatDuration(durationSec?: number | null) {
  if (!durationSec || Number.isNaN(durationSec)) {
    return '';
  }

  const minutes = Math.floor(durationSec / 60);
  const seconds = durationSec % 60;
  if (minutes <= 0) {
    return `${seconds}s`;
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function stringValue(value: unknown) {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return null;
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function firstString(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (value) {
      return value;
    }
  }
  return null;
}

function firstAllowedImageUrl(...values: Array<string | null | undefined>) {
  for (const value of values) {
    if (value && !isHotlinkBlockedBuzzImageUrl(value)) {
      return value;
    }
  }
  return null;
}

function isHotlinkBlockedBuzzImageUrl(value: string) {
  try {
    const parsed = new URL(value);
    return HOTLINK_BLOCKED_BUZZ_IMAGE_HOSTS.has(parsed.hostname.toLowerCase());
  } catch {
    return false;
  }
}
